import { useState } from "react";
import { MessageSquareQuote, ArrowUpRight, ArrowDownRight, Minus, Download, Loader2, ChevronRight, UserRound, CheckCheck, BookOpenCheck } from "lucide-react";
import { toast } from "sonner";
import { axiosInstance } from "../../App";
import { Button } from "@/components/ui/button";
import { triggerBlobDownload, blobErrorMessage } from "./ReportDownloadDialog";

const Change = ({ value, size = "sm" }) => {
  if (value === null || value === undefined) return null;
  const v = Math.round(value * 10) / 10;
  const cls = size === "lg" ? "text-sm px-2 py-0.5" : "text-[11px] px-1.5 py-0.5";
  if (v > 0) return <span className={`inline-flex items-center gap-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold ${cls}`}><ArrowUpRight className="w-3.5 h-3.5" />+{v}%</span>;
  if (v < 0) return <span className={`inline-flex items-center gap-0.5 rounded-full bg-red-100 text-red-600 font-bold ${cls}`}><ArrowDownRight className="w-3.5 h-3.5" />{v}%</span>;
  return <span className={`inline-flex items-center gap-0.5 rounded-full bg-gray-100 text-gray-600 font-bold ${cls}`}><Minus className="w-3.5 h-3.5" />0%</span>;
};

const Bar = ({ current, previous }) => (
  <div className="relative h-2 w-full rounded-full bg-gray-200 overflow-hidden">
    {previous !== null && previous !== undefined && (
      <div className="absolute inset-y-0 left-0 bg-purple-200" style={{ width: `${Math.min(previous, 100)}%` }} />
    )}
    <div className="absolute inset-y-0 left-0 bg-purple-600 rounded-full transition-[width] duration-700" style={{ width: `${Math.min(current, 100)}%` }} />
  </div>
);

function ChildTermCard({ item, onOpen }) {
  const [downloading, setDownloading] = useState(false);
  const [acking, setAcking] = useState(false);
  const [readAt, setReadAt] = useState(item.is_read ? item.read_at : null);
  const { latest, previous } = item;

  const acknowledge = async (e) => {
    e.stopPropagation();
    setAcking(true);
    try {
      const res = await axiosInstance.post(`/development/report-comment/${item.child_id}/acknowledge`, { term: latest.term_key });
      setReadAt(res.data?.read_at || new Date().toISOString());
      toast.success("Terima kasih! Penyelia dan pengurus telah dimaklumkan.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ralat merekod bacaan");
    } finally {
      setAcking(false);
    }
  };
  const latestPct = latest?.progress_snapshot?.percentage;
  const prevPct = previous?.progress_snapshot?.percentage;

  const download = async (e) => {
    e.stopPropagation();
    setDownloading(true);
    try {
      const res = await axiosInstance.get(`/development/report/${item.child_id}`, { params: { term: latest.term_key }, responseType: "blob" });
      triggerBlobDownload(res.data, `Laporan_Perkembangan_${item.child_name.replace(/\s+/g, "_")}_${latest.term_key}.pdf`, "application/pdf");
      toast.success("Laporan PDF dimuat turun!");
    } catch (error) {
      toast.error(await blobErrorMessage(error, "Ralat memuat turun laporan"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid={`term-report-${item.child_id}`} className="bg-white/95 rounded-2xl border border-purple-100 shadow-sm p-4 sm:p-5 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-black text-gray-800 truncate">{item.child_name}</p>
          <p className="text-xs text-gray-500 truncate">{item.class_name}</p>
        </div>
        <span className="shrink-0 rounded-full bg-purple-100 text-purple-800 text-xs font-bold px-2.5 py-1">{latest.term_label}</span>
      </div>

      {/* Perbandingan kemajuan */}
      <div className="mt-4 rounded-xl bg-purple-50/70 p-3">
        <div className="flex items-end justify-between gap-2">
          <div>
            <p className="text-[11px] uppercase tracking-wide font-bold text-purple-700">Kemajuan Penggal Ini</p>
            <p data-testid="term-report-percentage" className="text-3xl font-black text-gray-900 leading-none mt-1">{latestPct ?? 0}%</p>
          </div>
          <div className="text-right">
            <Change value={item.change} size="lg" />
            <p className="text-[11px] text-gray-500 mt-1">
              {previous ? `berbanding ${previous.term_label} (${prevPct ?? 0}%)` : "Penggal pertama direkodkan"}
            </p>
          </div>
        </div>
        <div className="mt-3 space-y-2">
          {item.domains.map((d) => (
            <div key={d.category}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-semibold text-gray-700">{d.label}</span>
                <span className="flex items-center gap-1.5 text-gray-600">
                  {d.percentage}%
                  <Change value={d.change} />
                </span>
              </div>
              <Bar current={d.percentage} previous={d.previous_percentage} />
            </div>
          ))}
        </div>
        {previous && (
          <div className="flex items-center gap-3 mt-3 text-[11px] text-gray-500">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-purple-600" /> {latest.term_label}</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-2 rounded-sm bg-purple-200" /> {previous.term_label}</span>
          </div>
        )}
      </div>

      {/* Komen guru */}
      <div className="mt-4 flex-1">
        <div className="relative rounded-xl border-l-4 border-purple-400 bg-white pl-3 pr-2 py-2">
          <MessageSquareQuote className="w-4 h-4 text-purple-400 mb-1" />
          <p data-testid="term-report-comment" className="text-sm text-gray-700 whitespace-pre-line line-clamp-6">{latest.comment}</p>
          <p className="flex items-center gap-1 text-[11px] text-gray-500 mt-2">
            <UserRound className="w-3.5 h-3.5" />
            {latest.teacher_name || "Guru"}{latest.updated_at ? ` · ${new Date(latest.updated_at).toLocaleDateString("ms-MY")}` : ""}
          </p>
        </div>
      </div>

      <div className="mt-4">
        {readAt ? (
          <div data-testid={`term-report-read-${item.child_id}`} className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 text-sm font-semibold">
            <CheckCheck className="w-4 h-4" />
            Sudah dibaca pada {new Date(readAt).toLocaleString("ms-MY", { dateStyle: "medium", timeStyle: "short" })}
          </div>
        ) : (
          <Button
            data-testid={`term-report-ack-${item.child_id}`} onClick={acknowledge} disabled={acking}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold"
          >
            {acking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BookOpenCheck className="w-4 h-4 mr-2" />}
            Saya sudah baca
          </Button>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          size="sm" data-testid={`term-report-pdf-${item.child_id}`} onClick={download} disabled={downloading}
          className="bg-purple-700 hover:bg-purple-800 text-white rounded-xl"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Download className="w-4 h-4 mr-1.5" />}
          Laporan PDF
        </Button>
        <Button size="sm" variant="outline" onClick={onOpen} className="rounded-xl border-purple-200 text-purple-700 hover:bg-purple-50">
          Lihat Perkembangan <ChevronRight className="w-4 h-4 ml-0.5" />
        </Button>
      </div>
    </div>
  );
}

// Kad dashboard ibu bapa: komen guru penggal terkini + perbandingan kemajuan
export default function ParentTermReport({ items, onOpen }) {
  if (!items?.length) return null;
  return (
    <div data-testid="parent-term-report" className={`grid grid-cols-1 gap-4 ${items.length > 1 ? "lg:grid-cols-2" : "max-w-3xl"}`}>
      {items.map((item) => (
        <ChildTermCard key={item.child_id} item={item} onOpen={onOpen} />
      ))}
    </div>
  );
}
